import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import { getLocales } from 'assets/locales/Locale';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import { compact, flatten, isEmpty, uniqBy } from 'lodash';
import { useContractProvider } from '../Contract.Hooks';

export default function ContractFilters() {
  const { lang } = getLocales();
  const {
    listApi,
    statuses,
    dealersApi,
    distributorsApi,
    modelsApi,
    userListApi,
    pageRouter,
    isDealer,
    isDistributor,
  } = useContractProvider();

  return (
    <>
      <FilterForm
        subcomponents={ThemeFilterFormDrawer}
        filters={listApi.body}
        onFilter={(newFilters) =>
          pageRouter.redirect(
            { ...pageRouter.params, action: undefined },
            {
              ...pageRouter.query,
              list: {
                ...newFilters,
                offset: 0,
              },
            }
          )
        }
        onReset={() => {
          pageRouter.redirect(
            { ...pageRouter.params, action: undefined },
            {
              ...pageRouter.query,
              list: undefined,
            }
          );
        }}
        onOpen={() => {
          if (!dealersApi.payload) dealersApi.execute((body) => body);
          if (!distributorsApi.payload) distributorsApi.execute((body) => body);
          if (!modelsApi.payload) modelsApi.execute((body) => body);
        }}
        render={({ tempFilters, setFilters }) => {
          const dealers = dealersApi?.payload?.data ?? [];
          const distributors = distributorsApi?.payload?.data ?? [];
          const users = userListApi?.payload?.data ?? [];

          const products = uniqBy(
            !isEmpty(tempFilters?.dealerId)
              ? flatten(dealers.filter((d) => tempFilters?.dealerId?.some((id) => id === d.id)).map((d) => d.products))
              : flatten(dealers.map((d) => d.products)),
            (p) => p.id
          );

          const makes =
            compact(
              uniqBy(
                modelsApi?.payload?.data?.map((m) => m.vehicleMake),
                (m) => m.id
              )
            ) ?? [];

          const models =
            modelsApi?.payload?.data?.filter((m) =>
              tempFilters?.makeCode?.some((code) => code === m.vehicleMake?.makeCode)
            ) ?? [];

          return (
            <>
              <TextInput
                name="contractNumber"
                label={lang.contractNumber}
                value={tempFilters?.contractNumber}
                onChange={setFilters}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="vin"
                label={lang.vin}
                value={tempFilters?.vin}
                onChange={setFilters}
                icon={<Icon class="if-quill-pen" />}
              />
              <TextInput
                name="clientName"
                label={lang.clientName}
                value={tempFilters?.clientName}
                onChange={setFilters}
                icon={<Icon class="if-quill-pen" />}
              />
              <SelectInput
                name="dealerId"
                label={lang.dealers}
                data={dealers.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                value={tempFilters?.dealerId}
                disabled={!dealers.length}
                onChange={setFilters}
                icon={<Icon class="fas-cloud" />}
              />
              {!isDealer && !isDistributor && (
                <SelectInput
                  name="distributorId"
                  label={lang.distributors}
                  data={distributors.map((item) => ({
                    ...item.displayInfo,
                    icon: <Icon class="fas-cloud" />,
                  }))}
                  isMultiselect
                  value={tempFilters?.distributorId}
                  disabled={!distributors.length}
                  onChange={setFilters}
                  icon={<Icon class="fas-cloud" />}
                />
              )}
              <SelectInput
                name="productId"
                label={lang.products}
                data={products.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-box" />,
                }))}
                isMultiselect
                value={tempFilters?.productId}
                onChange={setFilters}
                icon={<Icon class="fas-box" />}
              />
              <SelectInput
                name="statusCode"
                label={lang.status}
                data={statuses.map((item) => ({
                  id: item.id,
                  title: item.title,
                  icon: <Icon class="fas-check" />,
                }))}
                isMultiselect
                value={tempFilters?.statusCode}
                onChange={setFilters}
                icon={<Icon class="fas-cloud" />}
              />
              <SelectInput
                name="createdById"
                label={lang.createdBy}
                data={users?.map((user) => ({
                  id: user.id,
                  title: user.fullName,
                  icon: <Icon class="fas-user" />,
                }))}
                isMultiselect
                value={tempFilters?.createdById}
                onChange={setFilters}
                icon={<Icon class="fas-user" />}
              />
              <DateInput
                name="effectiveFromDateUtc"
                label={lang.effectiveFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.effectiveFromDateUtc ?? null}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="effectiveToDateUtc"
                label={lang.effectiveToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.effectiveToDateUtc ?? null}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="expiryFromDateUtc"
                label={lang.expiryFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.expiryFromDateUtc ?? null}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="expiryToDateUtc"
                label={lang.expiryToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.expiryToDateUtc ?? null}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="createdAtFromDateUtc"
                label={lang.createdAtFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.createdAtFromDateUtc}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="createdAtToDateUtc"
                label={lang.createdAtToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.createdAtToDateUtc}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="modifiedAtFromDateUtc"
                label={lang.modifiedAtFromDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.modifiedAtFromDateUtc}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <DateInput
                name="modifiedAtToDateUtc"
                label={lang.modifiedAtToDate}
                dateTimeFormat={defaultInputDateTimeFormat}
                value={tempFilters?.modifiedAtToDateUtc}
                onChange={setFilters}
                icon={<Icon class="fas-calendar-alt" />}
              />
              <SelectInput
                name="makeCode"
                label={lang.vehicleMake}
                data={makes.map((item) => ({
                  id: item.makeCode,
                  title: item.makeCode,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                value={tempFilters?.makeCode}
                disabled={!makes.length}
                onChange={setFilters}
                icon={<Icon class="fas-cloud" />}
              />
              <SelectInput
                name="vehicleModelId"
                label={lang.vehicleModel}
                data={models.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                value={tempFilters?.vehicleModelId}
                disabled={!models.length}
                onChange={setFilters}
                icon={<Icon class="fas-cloud" />}
              />
              <NumberInput
                name="vehicleYear"
                label={lang.vehicleYear}
                value={tempFilters?.vehicleYear}
                onChange={setFilters}
                icon={<Icon class="if-quill-pen" />}
              />
            </>
          );
        }}
      />
    </>
  );
}
